// src/components/report/PartA2.jsx - Exact match to reference design
import React from 'react';

// Star SVG component for decorative stars
const StarIcon = ({className, size = 20, fill = "currentColor", style = {}}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={fill}
        className={className}
        style={style}
    >
        <polygon
            points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
);

// Smiley face SVG component
const SmileyFace = ({size = 30, fill = "#FFD700"}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 100 100"
    >
        <circle cx="50" cy="50" r="45" fill={fill} stroke="#000" strokeWidth="2"/>
        <circle cx="30" cy="35" r="3" fill="#000"/>
        <circle cx="70" cy="35" r="3" fill="#000"/>
        <circle cx="35" cy="30" r="2" fill="#FFF"/>
        <circle cx="65" cy="30" r="2" fill="#FFF"/>
        <path d="M30 65 Q50 80 70 65" fill="none" stroke="#000" strokeWidth="3"/>
    </svg>
);

// Red happy face SVG component
const HappyFace = ({size = 30}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 100 100"
    >
        <circle cx="50" cy="50" r="45" fill="#DC2626" stroke="#000" strokeWidth="2"/>
        <circle cx="30" cy="35" r="3" fill="#FFF"/>
        <circle cx="70" cy="35" r="3" fill="#FFF"/>
        <circle cx="35" cy="30" r="2" fill="#FFF"/>
        <circle cx="65" cy="30" r="2" fill="#FFF"/>
        <path d="M30 65 Q50 80 70 65" fill="none" stroke="#FFF" strokeWidth="3"/>
    </svg>
);

// House icon SVG component
const HouseIcon = ({size = 24, color = "#3B82F6"}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
);

const PartA2 = ({data, pageNumber}) => {
    const safeData = data || {};

    // Format birthday if available
    const formattedBirthday = safeData.birthday
        ? new Date(safeData.birthday).toLocaleDateString('en-GB')
        : '';

    return (
        <div className="report-page relative bg-white" style={{
            width: '210mm',
            height: '297mm',
            minHeight: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.4',
            fontFamily: 'Arial, Helvetica, sans-serif',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            backgroundColor: '#FFFBEB' // Light cream background
        }}>
            {/* Title */}
            <div className="text-center text-orange-600 font-bold text-xl mb-6 mt-2">
                PART-A (2)
            </div>

            {/* ME AND MY SURROUNDINGS header */}
            <div className="relative mb-8">
                <div
                    className="bg-orange-600 text-white font-bold text-2xl py-3 px-8 rounded-full text-center relative mx-8"
                    style={{backgroundColor: '#EA580C'}}>
                    <StarIcon size={24} fill="white" className="absolute left-4 top-1/2 transform -translate-y-1/2"/>
                    ME AND MY SURROUNDINGS
                    <StarIcon size={24} fill="white" className="absolute right-4 top-1/2 transform -translate-y-1/2"/>
                </div>
            </div>

            {/* Main content area */}
            <div className="relative h-96 mb-8">
                {/* THIS IS ME section - top left */}
                <div className="absolute" style={{top: '0px', left: '20px', width: '220px', height: '140px'}}>
                    {/* Stars around the box */}
                    <StarIcon size={20} fill="#F59E0B" className="absolute" style={{top: '-15px', left: '60px'}}/>
                    <StarIcon size={16} fill="#10B981" className="absolute" style={{top: '-10px', left: '-10px'}}/>
                    <StarIcon size={14} fill="#EC4899" className="absolute" style={{top: '20px', right: '-10px'}}/>
                    <StarIcon size={18} fill="#3B82F6" className="absolute" style={{bottom: '-10px', left: '-15px'}}/>

                    {/* THIS IS text - angled */}
                    <div className="absolute text-black font-bold text-lg transform -rotate-12"
                         style={{top: '-25px', left: '10px', fontFamily: 'Comic Sans MS, cursive'}}>
                        THIS IS
                    </div>

                    {/* ME box */}
                    <div
                        className="border-2 border-black rounded-lg w-48 h-32 flex items-center justify-center bg-yellow-50 relative">
                        <div className="text-black font-black text-6xl"
                             style={{fontFamily: 'Comic Sans MS, cursive'}}>
                            ME
                        </div>
                    </div>
                </div>

                {/* I AM YEARS OLD - top right */}
                <div className="absolute" style={{top: '0px', right: '120px'}}>
                    <div className="flex items-center text-orange-700 font-bold text-lg"
                         style={{fontFamily: 'Comic Sans MS, cursive'}}>
                        I AM
                        <span
                            className="bg-white rounded-full px-4 py-1 mx-2 border border-orange-300 min-w-[60px] text-center">
                            {safeData.age_years || ''}
                        </span>
                        YEARS OLD
                        <StarIcon size={20} fill="#F59E0B" className="ml-2"/>
                    </div>
                </div>

                {/* My Birthday balloon - top right corner */}
                <div className="absolute" style={{top: '0px', right: '0px'}}>
                    <div className="relative">
                        <div
                            className="bg-purple-600 text-white rounded-full w-28 h-28 flex flex-col items-center justify-center text-center"
                            style={{backgroundColor: '#9333EA'}}>
                            <div className="text-white font-bold text-xs mb-1"
                                 style={{fontFamily: 'Comic Sans MS, cursive'}}>
                                My Birthday<br/>is on
                            </div>
                            <div
                                className="bg-white text-purple-600 w-20 h-6 rounded-md flex items-center justify-center font-bold text-xs">
                                {formattedBirthday}
                            </div>
                            {/* Balloon string */}
                            <div
                                className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 w-1 h-12 bg-purple-600"></div>
                            {/* Balloon highlight */}
                            <div className="absolute top-2 right-2 bg-white rounded-full w-3 h-3"></div>
                        </div>
                    </div>
                </div>

                {/* Smiley faces with family text */}
                <div className="absolute" style={{top: '120px', left: '0px'}}>
                    <SmileyFace size={32}/>
                    {/* Vertical green line */}
                    <div className="absolute top-0 left-8 w-1 h-16 bg-green-500"></div>
                </div>

                <div className="absolute" style={{top: '140px', left: '60px'}}>
                    <HappyFace size={28}/>
                    {/* Horizontal green line */}
                    <div className="absolute top-4 -left-8 h-1 w-20 bg-green-500"></div>
                </div>

                {/* This is my family text */}
                <div className="absolute text-red-500 font-bold text-sm"
                     style={{top: '180px', left: '0px', fontFamily: 'Comic Sans MS, cursive'}}>
                    This is<br/>my family
                </div>

                {/* I live in section - top right */}
                <div className="absolute" style={{top: '60px', right: '30px'}}>
                    <div className="text-blue-500 font-bold text-lg mb-2"
                         style={{fontFamily: 'Comic Sans MS, cursive'}}>
                        I live in
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 relative"
                         style={{width: '200px', height: '80px'}}>
                        <div className="border-b border-dotted border-blue-400 min-h-[1.2em] mb-2 text-sm pl-1 pb-1">
                            {safeData.live_in || ''}
                        </div>
                        <div className="border-b border-dotted border-blue-400 min-h-[1.2em] mb-2"></div>
                        <div className="border-b border-dotted border-blue-400 min-h-[1.2em]"></div>
                        <HouseIcon size={20} color="#3B82F6" className="absolute bottom-2 right-2"/>
                    </div>
                </div>

                {/* I WANT TO BE A section - bottom left */}
                <div className="absolute" style={{bottom: '0px', left: '0px'}}>
                    <div className="bg-white rounded-lg border-2 border-purple-500 p-4 relative"
                         style={{width: '200px', height: '70px'}}>
                        <div className="text-black font-bold text-sm mb-2"
                             style={{fontFamily: 'Comic Sans MS, cursive'}}>
                            I WANT TO BE A
                        </div>
                        <div className="border-b-2 border-black min-h-[1.2em] mb-2 text-sm pl-1 pb-1">
                            {safeData.want_to_be || ''}
                        </div>
                        <div className="text-right font-bold text-sm"
                             style={{fontFamily: 'Comic Sans MS, cursive'}}>
                            WHEN I GROW UP
                        </div>
                    </div>
                    <StarIcon size={18} fill="#3B82F6" className="absolute -bottom-2 right-8"/>
                </div>

                {/* Friends section - bottom right */}
                <div className="absolute" style={{bottom: '0px', right: '0px'}}>
                    <StarIcon size={24} fill="#EF4444" className="absolute -top-8 -left-8"/>
                    <div className="bg-white rounded-lg border-2 border-blue-500 p-3 relative"
                         style={{width: '220px', height: '120px'}}>
                        <div className="space-y-2">
                            {/* Render friend names */}
                            {safeData.friends_are?.split(',').slice(0, 5).map((friend, idx) => (
                                friend.trim() && (
                                    <div key={idx} className="flex items-center">
                                        <span className="text-purple-500 text-lg mr-2">•</span>
                                        <div
                                            className="border-b border-dotted border-blue-400 flex-grow min-h-[1.2em] text-xs pl-1 pb-1">
                                            {friend.trim()}
                                        </div>
                                    </div>
                                )
                            ))}
                            {/* Add empty lines if needed */}
                            {(!safeData.friends_are || safeData.friends_are.split(',').filter(f => f.trim()).length < 5) &&
                                Array(5 - (safeData.friends_are?.split(',').filter(f => f.trim()).length || 0))
                                    .fill(0).map((_, idx) => (
                                    <div key={`empty-${idx}`} className="flex items-center">
                                        <span className="text-purple-500 text-lg mr-2">•</span>
                                        <div
                                            className="border-b border-dotted border-blue-400 flex-grow min-h-[1.2em]"></div>
                                    </div>
                                ))
                            }
                        </div>
                        <StarIcon size={18} fill="#F59E0B" className="absolute bottom-2 right-2"/>
                    </div>
                    {/* are my friends badge */}
                    <div
                        className="absolute -bottom-2 right-6 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold"
                        style={{fontFamily: 'Comic Sans MS, cursive'}}>
                        are my friends
                    </div>
                </div>
            </div>

            {/* My Favourite Section */}
            <div className="bg-white rounded-lg border-2 border-yellow-400 p-4 relative mt-8">
                <StarIcon size={20} fill="#3B82F6" className="absolute top-2 right-4"/>
                <StarIcon size={16} fill="#10B981" className="absolute -bottom-3 left-8"/>
                <StarIcon size={18} fill="#F59E0B" className="absolute -bottom-3 right-1/4"/>

                <h3 className="text-center text-blue-600 font-bold text-xl mb-4"
                    style={{fontFamily: 'Comic Sans MS, cursive'}}>
                    My Favourite :
                </h3>

                <div className="grid grid-cols-2 gap-4">
                    {[
                        {label: 'COLOUR', icon: '🎨', color: '#EC4899', value: safeData.favourite_colours},
                        {label: 'FLOWER', icon: '🌸', color: '#8B5CF6', value: safeData.favourite_flowers},
                        {label: 'FOOD', icon: '🍽️', color: '#F97316', value: safeData.favourite_foods},
                        {label: 'SPORT', icon: '🏃', color: '#EF4444', value: safeData.favourite_sports},
                        {label: 'ANIMAL', icon: '🐾', color: '#10B981', value: safeData.favourite_animals},
                        {label: 'SUBJECT', icon: '📚', color: '#06B6D4', value: safeData.favourite_subjects},
                    ].map((fav, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                            <span className="text-lg">{fav.icon}</span>
                            <span className="font-bold text-sm w-16"
                                  style={{color: fav.color, fontFamily: 'Comic Sans MS, cursive'}}>
                                {fav.label}
                            </span>
                            <div className="flex-grow bg-orange-200 h-6 rounded-md flex items-center px-2 text-xs">
                                {fav.value || ''}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Page number */}
            {pageNumber && (
                <div className="absolute bottom-4 right-8 text-sm font-medium">
                    Page {pageNumber}
                </div>
            )}
        </div>
    );
};

export default PartA2;